import type { PlayerPublic } from "@aura-frontend/omnibus-az-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAppContext } from "@/hooks/use-app-context";
import {
  getMemberExternalId,
  getMemberJerseyNumber,
  getPlayerJerseyNumber,
} from "@/pages/TaggingV2/Scorekeeping/api/omnibusMappers";
import { findTeam } from "@/pages/TaggingV2/Scorekeeping/api/teamRoster";

export const TEAM_JERSEY_NUMBERS_QUERY_KEY = "teamJerseyNumbers";

/** Jersey numbers of a team's roster members, by player external id. */
export type TeamJerseyNumbers = ReadonlyMap<string, number>;

/**
 * Jersey numbers live on the team record's roster members, not the player,
 * and players search doesn't return them. A failed lookup only costs the
 * numbers, so it resolves empty rather than blocking the roster.
 */
const fetchTeamJerseyNumbers = async (
  teamExternalId: string,
  userOrgIds: string[],
): Promise<TeamJerseyNumbers> => {
  try {
    const record = await findTeam(teamExternalId, userOrgIds);
    return new Map(
      (record?.team.unofficial_members ?? []).flatMap((member) => {
        const externalId = getMemberExternalId(member);
        const jerseyNumber = getMemberJerseyNumber(member);
        return externalId && jerseyNumber != null
          ? [[externalId, jerseyNumber] as const]
          : [];
      }),
    );
  } catch (error) {
    console.warn(
      `[rosters] jersey number lookup failed for ${teamExternalId}`,
      error,
    );
    return new Map();
  }
};

const teamJerseyNumbersOptions = (
  teamExternalId: string,
  userOrgIds: string[],
) => ({
  queryKey: [TEAM_JERSEY_NUMBERS_QUERY_KEY, teamExternalId, userOrgIds],
  queryFn: () => fetchTeamJerseyNumbers(teamExternalId, userOrgIds),
  staleTime: 60_000,
});

/**
 * A player's jersey number for a lineup, resolved as Scorekeeping V2's
 * rosters do: the roster member's number, else the player's own. "" when
 * neither has one.
 */
export const getLineupJerseyNumber = (
  jerseyNumbers: TeamJerseyNumbers | undefined,
  player: PlayerPublic,
): string => {
  const jerseyNumber =
    jerseyNumbers?.get(player.external_ids?.[0] ?? "") ??
    getPlayerJerseyNumber(player);
  return jerseyNumber != null ? String(jerseyNumber) : "";
};

/**
 * The players with each one's lineup jersey number (see
 * getLineupJerseyNumber) as their own, for code that maps players on its own.
 */
export const withLineupJerseyNumbers = (
  players: PlayerPublic[],
  jerseyNumbers: TeamJerseyNumbers,
): PlayerPublic[] =>
  players.map((player) => {
    const jerseyNumber = getLineupJerseyNumber(jerseyNumbers, player);
    // `jersey_number` is served but missing from the generated type
    return jerseyNumber
      ? ({
          ...player,
          jersey_number: Number(jerseyNumber),
        } as PlayerPublic)
      : player;
  });

const useUserOrgIds = () =>
  useAppContext().userOrganizations.map((org) => org.orgId);

/** The team's roster jersey numbers; `isLoading` until they're known. */
export const useTeamJerseyNumbers = (teamExternalId: string | undefined) => {
  const userOrgIds = useUserOrgIds();
  return useQuery({
    ...teamJerseyNumbersOptions(teamExternalId ?? "", userOrgIds),
    enabled: !!teamExternalId,
  });
};

/** Fetches (or reuses) a team's roster jersey numbers outside render. */
export const useFetchTeamJerseyNumbers = () => {
  const userOrgIds = useUserOrgIds();
  const queryClient = useQueryClient();
  return (teamExternalId: string) =>
    queryClient.fetchQuery(
      teamJerseyNumbersOptions(teamExternalId, userOrgIds),
    );
};
